import { useNavigate } from "react-router";
import { WorkspacePermissions } from "@/core/permissions";
import { HasAnyPermission, HasAllPermissions } from "@/presentation/workspace";
import { EmptyState, Icons, Heading, Flex, Division, Paragraph, Button } from "@ir/ui";
import { Fragment } from "react/jsx-runtime";
import useDeductionIncomplete from "./hook/useDeductionIncomplete";
import { useUserPermissions } from "@/hooks";
import BackupDocuments from "./BackupDocuments";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import BackupRequestModal from "./backupRequest/BackupRequestModal";
import { WORKSPACE_ROUTES } from "@/routes/workspace/utils/Workspace.router.utils";

const renderTitle = () => {
  return (
    <Heading level={6} className="font-bold mt-3">
      We were unable to extract deduction details
    </Heading>
  );
};

const RenderDescription = ({
  setOpenBackupRequestModal,
  isBackupRequested,
  canRequestBackup,
}: {
  setOpenBackupRequestModal: (openBackupRequestModal: boolean) => void;
  isBackupRequested: boolean;
  canRequestBackup: boolean;
}) => {
  const { hasAnyPermission } = useUserPermissions();
  const navigate = useNavigate();

  return (
    <Fragment>
      <Flex direction="row" justifyContent="center">
        <Division className="font-regular pb-3">
          {hasAnyPermission([
            WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_REQUEST,
            WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_UPLOAD,
            WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_MANUAL,
          ]) && (
            <Paragraph style={{ textAlign: "left" }} className="mb-2">
              Choose how you'd like to move forward:
            </Paragraph>
          )}
          <ul className="m-0 p-0" style={{ listStyle: "inside", textAlign: "left" }}>
            {canRequestBackup && (
              <HasAllPermissions requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_REQUEST]}>
                <li className="mb-1">
                  <b>Request Backup:</b> Ask the purchaser to send the missing document
                </li>
              </HasAllPermissions>
            )}
            <HasAllPermissions
              requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_UPLOAD, WorkspacePermissions.INTELLI_UPLOAD_UPLOAD]}
            >
              <li className="mb-1">
                <b>Upload Backup:</b> Add a backup document from your files
              </li>
            </HasAllPermissions>
            <HasAllPermissions requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_MANUAL]}>
              <li className="mb-1">
                <b>Enter Manually:</b> Fill in the deduction details yourself
              </li>
            </HasAllPermissions>
          </ul>
        </Division>
      </Flex>
      <Flex direction="row" justifyContent="center">
        {canRequestBackup && (
          <HasAllPermissions requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_REQUEST]}>
            <Button color="primary" onPress={() => setOpenBackupRequestModal(true)} isDisabled={isBackupRequested}>
              {isBackupRequested ? "Backup Requested" : "Request Backup"}
            </Button>
          </HasAllPermissions>
        )}
        <HasAllPermissions requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_UPLOAD, WorkspacePermissions.INTELLI_UPLOAD_UPLOAD]}>
          <Button color="primary" onPress={() => navigate(`/workspace${WORKSPACE_ROUTES.INTELLI_UPLOAD}`)}>
            Upload Backup
          </Button>
        </HasAllPermissions>
        <HasAllPermissions requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_RESOLVE_BACKUP_MANUAL]}>
          <Button color="primary">Enter Manually</Button>
        </HasAllPermissions>
      </Flex>
    </Fragment>
  );
};

const DeductionIncompleteContent = () => {
  const { openBackupRequestModal, setOpenBackupRequestModal, isLoadingDeductionStatus, deductionResolutionStatus, importedFilesAsDocuments } =
    useDeductionIncomplete();
  const { drawerInfo } = useDeductionDrawer();

  return (
    <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTION_INCOMPLETE_VIEW]}>
      <EmptyState
        className="bg-white p-4"
        icon={Icons.FileSearch03}
        title={renderTitle()}
        description={
          <RenderDescription
            setOpenBackupRequestModal={setOpenBackupRequestModal}
            isBackupRequested={drawerInfo?.remittanceItem?.isBackupRequested ?? false}
            canRequestBackup={drawerInfo?.remittanceItem?.canRequestBackup ?? false}
          />
        }
      />
      <br />
      {!isLoadingDeductionStatus && deductionResolutionStatus !== "incomplete" && importedFilesAsDocuments.length > 0 && <BackupDocuments />}
      <BackupRequestModal
        isOpen={openBackupRequestModal}
        onClose={() => setOpenBackupRequestModal(false)}
        remittanceItemId={drawerInfo?.remittanceItemId ?? 0}
        purchaserKey={drawerInfo?.purchaserKey ?? ""}
      />
    </HasAnyPermission>
  );
};

export default DeductionIncompleteContent;
